import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Snackbar,
} from '@mui/material'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import GroupIcon from '@mui/icons-material/Group'
import Loader from '../Loader'
import PageHeader from '../Common/PageHeader'
import UserVouchers from './UserVouchers'
import {
  CREATE_USER,
  GET_USERS,
  UPDATE_USER,
  DELETE_USER,
} from '../../api/user'
import { GET_ROLES } from '../../api/role'
import { GET_DEPARTMENTS } from '../../api/department'
import { toast } from 'react-toastify'
import { Role } from '../../types'

interface User {
  id: string
  first_name: string
  last_name: string
  email: string
  branch: string
  site_admin: boolean
  created_at: string
  last_signed_in: string
  department?: {
    id: string
    name: string
  }
  user_role?: {
    id: number
    name: string
    scope: string
  }
}

interface Department {
  id: string
  name: string
}

const Users: React.FC = () => {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingUser, setEditingUser] = useState<Partial<User>>({})
  const [isAdding, setIsAdding] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [selectedUserForVouchers, setSelectedUserForVouchers] = useState<{
    id: string
    name: string
  } | null>(null)

  const { data: usersData, loading: usersLoading } = useQuery(GET_USERS)
  const [createUser] = useMutation(CREATE_USER, {
    refetchQueries: [{ query: GET_USERS }],
    awaitRefetchQueries: true,
  })
  const [updateUser] = useMutation(UPDATE_USER)
  const [deleteUser] = useMutation(DELETE_USER, {
    refetchQueries: [{ query: GET_USERS }],
    awaitRefetchQueries: true,
  })
  const { data: departmentsData, loading: departmentsLoading } =
    useQuery(GET_DEPARTMENTS)
  const { data: userRolesData, loading: userRolesLoading } = useQuery(GET_ROLES)

  const users: User[] = usersData?.users || []
  const departments: Department[] = departmentsData?.departments || []
  const userRoles: Role[] = userRolesData?.userRoles || []

  const handleEdit = (user: User) => {
    setEditingId(user.id)
    setEditingUser({ ...user })
  }

  const handleSave = async () => {
    try {
      if (isAdding) {
        const { data } = await createUser({
          variables: {
            input: {
              first_name: editingUser.first_name,
              last_name: editingUser.last_name,
              email: editingUser.email,
              password: '123', // TODO: default password, should be made better
              branch: editingUser.branch,
              site_admin: editingUser.site_admin,
              department: editingUser.department
                ? { connect: { id: editingUser.department.id } }
                : undefined,
              user_role: editingUser.user_role
                ? { connect: { id: editingUser.user_role.id } }
                : undefined,
            },
          },
        })
        if (data) {
          toast.success(`User ${data.createUser.first_name} created`)
          setSuccessMessage(`User ${data.createUser.first_name} created`)
          setShowSuccess(true)
          setIsAdding(false)
          setEditingUser({})
        }
      } else {
        const { data } = await updateUser({
          variables: {
            id: editingId,
            input: {
              first_name: editingUser.first_name,
              last_name: editingUser.last_name,
              email: editingUser.email,
              branch: editingUser.branch,
              site_admin: editingUser.site_admin,
              department: { connect: { id: editingUser.department?.id } },
              user_role: { connect: { id: editingUser.user_role?.id } },
            },
          },
        })
        if (data) {
          toast.success(`User ${data.createUser.first_name} updated`)
          setSuccessMessage(`User ${data.createUser.first_name} updated`)
          setEditingId(null)
          setEditingUser({})
        }
      }
    } catch (err: any) {
      console.error('Error updating voucher:', err.message)
      toast.error(err.message || `User with ID ${editingId} updated failed`)
    }
  }

  const handleDelete = async (user: User) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete user "${user.first_name} ${user.last_name}"?`
    )
    if (!confirmed) return

    try {
      await deleteUser({
        variables: { id: user.id },
      })
      toast.success(
        `User "${user.first_name} ${user.last_name}" deleted successfully`
      )
    } catch (err: any) {
      console.error('Error deleting user:', err.message)
      toast.error(err.message || 'Failed to delete user')
    }
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditingUser({})
    setIsAdding(false)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    let dateObj = new Date(dateString)
    if (isNaN(dateObj.getTime())) {
      // Try parsing as a number (timestamp)
      const asNumber = Number(dateString)
      if (!isNaN(asNumber)) {
        dateObj = new Date(asNumber)
      }
    }
    return isNaN(dateObj.getTime()) ? '-' : dateObj.toLocaleDateString()
  }

  if (usersLoading || departmentsLoading || userRolesLoading) {
    return <Loader minHeight={400} />
  }

  return (
    <Box>
      <PageHeader title="Users Management" icon={GroupIcon} />
      <IconButton
        title="Add User"
        onClick={() => {
          setIsAdding(true)
          setEditingUser({})
        }}
        sx={{ mb: 2 }}
      >
        <AddCircleIcon fontSize="medium" color="success" />
      </IconButton>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Site Admin</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>UserRole</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Sign In</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isAdding && (
              <TableRow>
                <TableCell>
                  <Box>
                    <TextField
                      size="small"
                      placeholder="First Name"
                      value={editingUser.first_name || ''}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          first_name: e.target.value,
                        })
                      }
                      sx={{ mr: 1, width: '120px' }}
                    />
                    <TextField
                      size="small"
                      placeholder="Last Name"
                      value={editingUser.last_name || ''}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          last_name: e.target.value,
                        })
                      }
                      sx={{ width: '120px' }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Email"
                    value={editingUser.email || ''}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, email: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small">
                    <Select
                      value={editingUser.site_admin ? 'Yes' : 'No'}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          site_admin: e.target.value === 'Yes',
                        })
                      }
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder="Branch"
                    value={editingUser.branch || ''}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, branch: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={editingUser.department?.id || ''}
                      onChange={(e) => {
                        const dept = departments.find(
                          (d) => d.id === e.target.value
                        )
                        setEditingUser({
                          ...editingUser,
                          department: dept
                            ? { id: dept.id, name: dept.name }
                            : undefined,
                        })
                      }}
                    >
                      <MenuItem value="">No Department</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={editingUser.user_role?.id || ''}
                      onChange={(e) => {
                        const uRole = userRoles.find(
                          (r) => r.id === Number(e.target.value)
                        )
                        setEditingUser({
                          ...editingUser,
                          user_role: uRole
                            ? {
                                id: uRole.id,
                                name: uRole.name,
                                scope: uRole.scope,
                              }
                            : undefined,
                        })
                      }}
                    >
                      <MenuItem value="" selected>
                        No Role
                      </MenuItem>

                      {['BRANCH', 'NATIONAL']
                        .map((scope) => [
                          <MenuItem
                            key={`parent-${scope}`}
                            value={`parent-${scope}`}
                            disabled
                            sx={{
                              fontWeight: 'bold',
                              backgroundColor: 'grey.100',
                              color: 'text.secondary',
                              '&:hover': { backgroundColor: 'grey.100' },
                            }}
                          >
                            {scope}
                          </MenuItem>,
                          userRoles
                            .filter((c) => c.scope === scope)
                            .map((ur) => (
                              <MenuItem
                                key={ur.id}
                                value={ur.id}
                                sx={{ pl: 4 }}
                              >
                                {ur.name}
                              </MenuItem>
                            )),
                        ])
                        .flat()}
                    </Select>
                  </FormControl>
                  {/* <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={editingUser.user_role?.id || ''}
                      onChange={(e) => {
                        const uRole = userRoles.find(
                          (d) => d.id === Number(e.target.value)
                        )
                        setEditingUser({
                          ...editingUser,
                          user_role: uRole
                            ? {
                                id: uRole.id,
                                name: uRole.name,
                                scope: uRole.scope,
                              }
                            : undefined,
                        })
                      }}
                    >
                      <MenuItem value="">No Role</MenuItem>
                      {userRoles.map((ur) => (
                        <MenuItem key={ur.id} value={ur.id}>
                          {ur.name} - {ur.scope}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl> */}
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={handleSave} color="primary">
                    <SaveIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleCancel}
                    color="secondary"
                  >
                    <CancelIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  {editingId === user.id ? (
                    <Box>
                      <TextField
                        size="small"
                        value={editingUser.first_name || ''}
                        onChange={(e) =>
                          setEditingUser({
                            ...editingUser,
                            first_name: e.target.value,
                          })
                        }
                        placeholder="First Name"
                        sx={{ mr: 1, width: '120px' }}
                      />
                      <TextField
                        size="small"
                        value={editingUser.last_name || ''}
                        onChange={(e) =>
                          setEditingUser({
                            ...editingUser,
                            last_name: e.target.value,
                          })
                        }
                        placeholder="Last Name"
                        sx={{ width: '120px' }}
                      />
                    </Box>
                  ) : (
                    `${user.first_name} ${user.last_name}`
                  )}
                </TableCell>
                <TableCell>
                  {editingId === user.id ? (
                    <TextField
                      size="small"
                      value={editingUser.email || ''}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          email: e.target.value,
                        })
                      }
                      placeholder="Email"
                    />
                  ) : (
                    user.email
                  )}
                </TableCell>
                <TableCell>
                  {editingId === user.id ? (
                    <FormControl size="small">
                      <Select
                        value={editingUser.site_admin ? 'Yes' : 'No'}
                        onChange={(e) =>
                          setEditingUser({
                            ...editingUser,
                            site_admin: e.target.value === 'Yes',
                          })
                        }
                      >
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>
                    </FormControl>
                  ) : user.site_admin ? (
                    'Yes'
                  ) : (
                    'No'
                  )}
                </TableCell>
                <TableCell>
                  {editingId === user.id ? (
                    <TextField
                      size="small"
                      value={editingUser.branch || ''}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          branch: e.target.value,
                        })
                      }
                      placeholder="Branch"
                    />
                  ) : (
                    user.branch || '-'
                  )}
                </TableCell>
                <TableCell>
                  {editingId === user.id ? (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={editingUser.department?.id || ''}
                        onChange={(e) => {
                          const dept = departments.find(
                            (d) => d.id === e.target.value
                          )
                          setEditingUser({
                            ...editingUser,
                            department: dept
                              ? { id: dept.id, name: dept.name }
                              : undefined,
                          })
                        }}
                      >
                        <MenuItem value="">No Department</MenuItem>
                        {departments.map((dept) => (
                          <MenuItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    user.department?.name || '-'
                  )}
                </TableCell>{' '}
                <TableCell>
                  {editingId === user.id ? (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={editingUser.user_role?.id || 0}
                        onChange={(e) => {
                          const uRole = userRoles.find(
                            (d) => d.id === Number(e.target.value)
                          )
                          setEditingUser({
                            ...editingUser,
                            user_role: uRole
                              ? {
                                  id: uRole.id,
                                  name: uRole.name,
                                  scope: uRole.scope,
                                }
                              : undefined,
                          })
                        }}
                      >
                        <MenuItem value="">No Role</MenuItem>
                        {userRoles.map((ur) => (
                          <MenuItem key={ur.id} value={ur.id}>
                            {ur.name + ' - ' + ur.scope}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    user.user_role?.name + ' - ' + user.user_role?.scope || '-'
                  )}
                </TableCell>
                <TableCell>{formatDate(user.created_at)}</TableCell>
                <TableCell>{formatDate(user.last_signed_in)}</TableCell>
                <TableCell>
                  <Box>
                    {editingId === user.id ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={handleSave}
                          color="primary"
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancel}
                          color="secondary"
                        >
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton size="small" onClick={() => handleEdit(user)}>
                        <EditIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() =>
                        setSelectedUserForVouchers({
                          id: user.id,
                          name: `${user.first_name} ${user.last_name}`,
                        })
                      }
                      color="info"
                    >
                      <VisibilityIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => handleDelete(user)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      {selectedUserForVouchers && (
        <UserVouchers
          userId={selectedUserForVouchers.id}
          userName={selectedUserForVouchers.name}
          open={!!selectedUserForVouchers}
          onClose={() => setSelectedUserForVouchers(null)}
        />
      )}
    </Box>
  )
}

export default Users
